import axios from "axios";

const ADMIN_API_BASE_URL = "/ofc/api/v1/admin/configurations";

// Configure axios defaults
axios.defaults.withCredentials = true;

export interface OfficeLocation {
  id?: string;
  name: string;
}

export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
  error?: string;
}

class OfficeLocationsApiService {
  private readonly CONFIG_KEY = "OFFICE_LOCATIONS";

  /**
   * Get all office locations
   */
  async getAllOfficeLocations(): Promise<OfficeLocation[]> {
    try {
      const response = await axios.get<ApiResponse<any>>(
        `${ADMIN_API_BASE_URL}/${this.CONFIG_KEY}`,
      );
      if (!response.data.success || !response.data.data) {
        return [];
      }

      const configValue = response.data.data.configValue;
      if (!configValue) {
        return [];
      }

      try {
        return JSON.parse(configValue);
      } catch {
        return [];
      }
    } catch (error) {
      console.error("Error fetching office locations:", error);
      return [];
    }
  }

  /**
   * Add a new office location
   */
  async addOfficeLocation(name: string): Promise<OfficeLocation[]> {
    const locations = await this.getAllOfficeLocations();

    // Check if location already exists
    if (
      locations.some((loc) => loc.name.toLowerCase() === name.toLowerCase())
    ) {
      throw new Error("Office location already exists");
    }

    const newLocation: OfficeLocation = {
      id: Date.now().toString(),
      name: name.trim(),
    };

    locations.push(newLocation);
    await this.saveOfficeLocations(locations);
    return locations;
  }

  /**
   * Update an office location
   */
  async updateOfficeLocation(
    id: string,
    name: string,
  ): Promise<OfficeLocation[]> {
    const locations = await this.getAllOfficeLocations();
    const index = locations.findIndex((loc) => loc.id === id);

    if (index === -1) {
      throw new Error("Office location not found");
    }

    // Check if new name already exists (excluding current location)
    if (
      locations.some(
        (loc, idx) =>
          idx !== index && loc.name.toLowerCase() === name.toLowerCase(),
      )
    ) {
      throw new Error("Office location name already exists");
    }

    locations[index].name = name.trim();
    await this.saveOfficeLocations(locations);
    return locations;
  }

  /**
   * Delete is not allowed - office locations cannot be deleted
   * This prevents issues with users already assigned to those locations
   */
  async deleteOfficeLocation(id: string): Promise<void> {
    throw new Error(
      "Office locations cannot be deleted to prevent data integrity issues",
    );
  }

  /**
   * Save office locations to configuration
   */
  private async saveOfficeLocations(
    locations: OfficeLocation[],
  ): Promise<void> {
    try {
      const response = await axios.put<ApiResponse<any>>(
        `${ADMIN_API_BASE_URL}/${this.CONFIG_KEY}`,
        {
          configKey: this.CONFIG_KEY,
          configValue: JSON.stringify(locations),
          description:
            "List of office locations available for employee assignment",
          isSensitive: false,
        },
      );

      if (!response.data.success) {
        throw new Error(
          response.data.error || "Failed to save office locations",
        );
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.error || "Failed to save office locations",
        );
      }
      throw error;
    }
  }
}

export default new OfficeLocationsApiService();
